/**
 * ReportsTab Component
 * Manages scheduled report delivery for events (event redemption reports).
 * Users can schedule a one-time CSV report to be emailed at a chosen date/time.
 */

import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEventReportSchedules,
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  SCHEDULE_NO_LONGER_EDITABLE,
} from "../../../services/reports.service";
import type { ReportSchedule } from "../../../types/event.types";
import LoadingSpinner from "../../common/LoadingSpinner";
import Button from "../../common/Button";
import { useToast } from "../../ui/Toast";

// Common US timezones
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
];

// Time options in 30-minute increments
const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? "AM" : "PM";
    const min = m === 0 ? "00" : "30";
    const val = `${String(h).padStart(2, "0")}:${min}`;
    const label = `${hour12}:${min} ${ampm}`;
    TIME_OPTIONS.push(`${val}|${label}`);
  }
}

interface ReportsTabProps {
  eventId: string;
  eventTimezone?: string;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Default timezone for create form + edit-open. R9 (2026-05-25): edit
// mode reuses the same priority chain as create — event-local tz first,
// browser/Eastern fallback — so the operator sees event-local time on
// both new and existing schedules.
const defaultTimezone = (eventTimezone?: string): string => {
  if (
    eventTimezone &&
    TIMEZONE_OPTIONS.some((tz) => tz.value === eventTimezone)
  ) {
    return eventTimezone;
  }
  return "America/New_York";
};

// R9 (2026-05-25): format a stored UTC scheduled_at into the create-form
// shape (date string, time string in 30-min slot) using a specific tz.
// Used when opening edit on an existing schedule so the form fields
// re-populate correctly.
const splitScheduledAt = (
  utcIso: string,
  tz: string,
): { date: string; time: string } => {
  const d = new Date(utcIso);
  // en-CA gives YYYY-MM-DD ordering which our <input type="date"> needs.
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  // Intl returns "24" instead of "00" for midnight in some locales.
  const hh = get("hour") === "24" ? "00" : get("hour");
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hh}:${get("minute")}`,
  };
};

const ReportsTab: React.FC<ReportsTabProps> = ({ eventId, eventTimezone }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [showForm, setShowForm] = useState(false);
  // R9 (2026-05-25): editingScheduleId disambiguates create vs edit. Null
  // = new-schedule form; populated = editing that schedule.
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(
    null,
  );
  // 2026-06-04: separate signal for duplicate mode (per audit R2 must-fix).
  // When non-null, the form is creating a NEW schedule (editingScheduleId
  // is also null) but the header copy reads "Duplicate schedule (from
  // <source-date>)". Cleared on resetForm, save success, and on any
  // mode switch (openEditForm + setShowForm(true) for a fresh create).
  const [duplicatingFromScheduleDate, setDuplicatingFromScheduleDate] =
    useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("16:30");
  const [timezone, setTimezone] = useState(() =>
    defaultTimezone(eventTimezone),
  );
  const [attachmentFormat, setAttachmentFormat] = useState<"csv" | "xlsx">(
    "csv",
  );
  const [dateTimeError, setDateTimeError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Fetch existing schedules
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["report-schedules", eventId],
    queryFn: () => getEventReportSchedules(eventId),
    enabled: !!eventId,
  });

  // Create mutation. R9 (2026-05-25): existing showToast calls in this
  // file had message/variant swapped vs. the (type, message) signature
  // declared in Toast.tsx — fixed inline. Other call sites in the
  // codebase use the correct order.
  const createMutation = useMutation({
    mutationFn: createReportSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["report-schedules", eventId],
      });
      showToast("success", "Report scheduled successfully");
      resetForm();
    },
    onError: (error: any) => {
      showToast("error", error.message || "Failed to schedule report");
    },
  });

  // R9 (2026-05-25): Update mutation. Wired to the hardened
  // updateReportSchedule which adds last_sent_at/is_active predicates
  // server-side and surfaces SCHEDULE_NO_LONGER_EDITABLE when the row
  // has been sent or deactivated mid-edit.
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        recipient_emails?: string[];
        scheduled_at?: string;
        attachment_format?: "csv" | "xlsx";
      };
    }) => updateReportSchedule(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["report-schedules", eventId],
      });
      showToast("success", "Report schedule updated");
      resetForm();
    },
    onError: (error: any) => {
      if (error?.message === SCHEDULE_NO_LONGER_EDITABLE) {
        showToast(
          "error",
          "This schedule has already been sent or was deactivated. Refreshing the list.",
        );
        queryClient.invalidateQueries({
          queryKey: ["report-schedules", eventId],
        });
        resetForm();
      } else {
        showToast("error", error?.message || "Failed to update schedule");
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteReportSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["report-schedules", eventId],
      });
      showToast("success", "Report schedule deleted");
      setConfirmDelete(null);
    },
    onError: (error: any) => {
      showToast("error", error.message || "Failed to delete schedule");
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingScheduleId(null);
    setDuplicatingFromScheduleDate(null);
    setEmailInput("");
    setEmails([]);
    setEmailError("");
    setScheduledDate("");
    setScheduledTime("16:30");
    setTimezone(defaultTimezone(eventTimezone));
    setAttachmentFormat("csv");
    setDateTimeError("");
    setIsSaving(false);
  };

  // 2026-06-04: open the form in duplicate-mode pre-populated from
  // an existing schedule's values. Differences from openEditForm:
  //   - editingScheduleId stays null (save -> INSERT new row).
  //   - duplicatingFromScheduleDate is set for the header label.
  //   - scheduledDate is cleared so operator must pick a new date.
  //   - scheduledTime is kept (promoters typically reuse the same
  //     end-of-day send window).
  //   - timezone resets to defaultTimezone(eventTimezone) — schedules
  //     don't persist a timezone, so this matches create-form behavior.
  const openDuplicateForm = (schedule: ReportSchedule) => {
    const tz = defaultTimezone(eventTimezone);
    setEditingScheduleId(null);
    setEmails(schedule.recipient_emails);
    setEmailInput("");
    setEmailError("");
    if (schedule.scheduled_at) {
      const { date: srcDate, time } = splitScheduledAt(
        schedule.scheduled_at,
        tz,
      );
      setDuplicatingFromScheduleDate(srcDate);
      setScheduledDate("");
      setScheduledTime(time);
    } else {
      setDuplicatingFromScheduleDate(null);
      setScheduledDate("");
      setScheduledTime("16:30");
    }
    setTimezone(tz);
    // Carry forward the source's attachment_format so a duplicate of an
    // xlsx schedule defaults to xlsx (matches operator intent — they
    // chose xlsx for the original).
    setAttachmentFormat(schedule.attachment_format ?? "csv");
    setDateTimeError("");
    setShowForm(true);
  };

  // R9 (2026-05-25): open the form pre-populated with an existing
  // schedule's values. Timezone defaults to eventTimezone (mirrors
  // create-form behavior) so date/time fields show event-local moment.
  const openEditForm = (schedule: ReportSchedule) => {
    const tz = defaultTimezone(eventTimezone);
    setEditingScheduleId(schedule.id);
    setDuplicatingFromScheduleDate(null);
    setEmails(schedule.recipient_emails);
    setEmailInput("");
    setEmailError("");
    if (schedule.scheduled_at) {
      const { date, time } = splitScheduledAt(schedule.scheduled_at, tz);
      setScheduledDate(date);
      setScheduledTime(time);
    } else {
      setScheduledDate("");
      setScheduledTime("16:30");
    }
    setTimezone(tz);
    setAttachmentFormat(schedule.attachment_format ?? "csv");
    setDateTimeError("");
    setShowForm(true);
  };

  const addEmail = () => {
    const trimmed = emailInput.trim().toLowerCase();
    setEmailError("");
    if (!trimmed) {
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError(
        "Please enter an email address with a valid format, such as name@example.com.",
      );
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailError(
        "This recipient has already been added. Please add a new email.",
      );
      return;
    }
    setEmails([...emails, trimmed]);
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  // Clear date/time error when inputs change
  useEffect(() => {
    setDateTimeError("");
  }, [scheduledDate, scheduledTime]);

  const handleSubmit = async () => {
    setEmailError("");
    setDateTimeError("");

    if (emails.length === 0) {
      setEmailError("Add at least one recipient email.");
      return;
    }
    if (!scheduledDate) {
      setDateTimeError("Please select a date for the report.");
      return;
    }

    // Build a Date in the selected timezone
    const dateTimeStr = `${scheduledDate}T${scheduledTime}:00`;
    // For comparison, use a simple approach: construct the ISO string in the tz
    // We'll use Intl to get the offset then build the proper UTC time
    const localDate = new Date(dateTimeStr);
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(localDate);
    const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value;

    // Parse offset like "GMT-5" or "GMT+5:30"
    let offsetMinutes = 0;
    if (offsetPart) {
      const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (match) {
        const sign = match[1] === "+" ? 1 : -1;
        const hours = parseInt(match[2], 10);
        const mins = match[3] ? parseInt(match[3], 10) : 0;
        offsetMinutes = sign * (hours * 60 + mins);
      }
    }

    // Convert local time in timezone to UTC
    const utcMs =
      localDate.getTime() -
      localDate.getTimezoneOffset() * 60000 -
      offsetMinutes * 60000;
    const scheduledUtc = new Date(utcMs);

    if (scheduledUtc <= new Date()) {
      setDateTimeError(
        "Selected time occurs in the past. Please choose a different time.",
      );
      return;
    }

    setIsSaving(true);
    try {
      if (editingScheduleId) {
        // R9 (2026-05-25): edit existing schedule. Only recipient_emails,
        // scheduled_at, and attachment_format are mutable; report_type
        // / is_recurring are locked post-create.
        await updateMutation.mutateAsync({
          id: editingScheduleId,
          updates: {
            recipient_emails: emails,
            scheduled_at: scheduledUtc.toISOString(),
            attachment_format: attachmentFormat,
          },
        });
      } else {
        await createMutation.mutateAsync({
          report_type: "event_redemption",
          event_id: eventId,
          recipient_emails: emails,
          scheduled_at: scheduledUtc.toISOString(),
          attachment_format: attachmentFormat,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatScheduleDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  // Today's date string for the min attribute
  const todayStr = new Date().toISOString().slice(0, 10);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Scheduled Reports
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Schedule a one-time CSV report of all vouchers (redeemed and
            unredeemed) to be emailed at a specific date and time. Test and
            cancelled vouchers are excluded.
          </p>
        </div>
        {!showForm && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              // Fresh create — clear any lingering edit/duplicate state.
              setEditingScheduleId(null);
              setDuplicatingFromScheduleDate(null);
              setShowForm(true);
            }}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Schedule Report
          </Button>
        )}
      </div>

      {/* Schedule Form — same JSX serves create, edit, and duplicate.
          editingScheduleId controls edit vs new (save UPDATE vs INSERT).
          duplicatingFromScheduleDate signals duplicate mode for the
          header label only — save still INSERTs because editingScheduleId
          is null. */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
          <h3 className="text-sm font-medium text-gray-900">
            {editingScheduleId
              ? "Edit Report Schedule"
              : duplicatingFromScheduleDate
                ? `Duplicate schedule (from ${duplicatingFromScheduleDate})`
                : "New Report Schedule"}
          </h3>

          {/* Email input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Emails
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={handleEmailKeyDown}
                placeholder="Enter email and press Enter"
                className={`flex-1 rounded-md shadow-sm text-sm ${
                  emailError
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                }`}
              />
              <Button variant="secondary" size="sm" onClick={addEmail}>
                Add
              </Button>
            </div>
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}

            {/* Email chips */}
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    <EnvelopeIcon className="w-3 h-3 mr-1" />
                    {email}
                    <button
                      onClick={() => removeEmail(email)}
                      className="ml-1.5 text-primary-600 hover:text-primary-900"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Date picker */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={todayStr}
                className={`w-full rounded-md shadow-sm text-sm ${
                  dateTimeError
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <select
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className={`w-full rounded-md shadow-sm text-sm ${
                  dateTimeError
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                }`}
              >
                {TIME_OPTIONS.map((opt) => {
                  const [val, label] = opt.split("|");
                  return (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              {eventTimezone &&
                TIMEZONE_OPTIONS.some((tz) => tz.value === eventTimezone) && (
                  <p className="mt-1 text-xs text-gray-400">
                    Defaults to event timezone
                  </p>
                )}
            </div>
          </div>

          {/* Attachment format — added 2026-06-04 with the XLSX
              scheduled-reports ship. Defaults to CSV; opt in to xlsx
              per-schedule. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment Format
            </label>
            <div className="flex gap-3">
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  name="attachment_format"
                  value="csv"
                  checked={attachmentFormat === "csv"}
                  onChange={() => setAttachmentFormat("csv")}
                  className="mr-2 text-primary-600 focus:ring-primary-500"
                />
                CSV
              </label>
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  name="attachment_format"
                  value="xlsx"
                  checked={attachmentFormat === "xlsx"}
                  onChange={() => setAttachmentFormat("xlsx")}
                  className="mr-2 text-primary-600 focus:ring-primary-500"
                />
                Excel (.xlsx) — bold headers, sized columns, sorted
              </label>
            </div>
          </div>

          {dateTimeError && (
            <p className="text-sm text-red-600">{dateTimeError}</p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSaving || emails.length === 0 || !scheduledDate}
            >
              {isSaving
                ? "Saving..."
                : editingScheduleId
                  ? "Update Schedule"
                  : "Save Schedule"}
            </Button>
            <Button variant="secondary" size="sm" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Existing Schedules */}
      {schedules.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <ClockIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            No report schedules yet. Click "Schedule Report" to set one up.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule: ReportSchedule) => (
            <div
              key={schedule.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between"
            >
              <div className="space-y-1.5">
                {/* Status badge */}
                <div className="flex items-center gap-2">
                  {schedule.last_sent_at ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      Sent
                    </span>
                  ) : schedule.is_active ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      Scheduled
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                </div>

                {/* Schedule date */}
                {schedule.scheduled_at && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Scheduled:</span>{" "}
                    {formatScheduleDate(schedule.scheduled_at)}
                  </p>
                )}

                {/* Sent date */}
                {schedule.last_sent_at && (
                  <p className="text-sm text-gray-500">
                    Sent: {formatScheduleDate(schedule.last_sent_at)}
                    {schedule.send_count > 1 &&
                      ` (${schedule.send_count} times)`}
                  </p>
                )}

                {/* Recipients */}
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {schedule.recipient_emails.map((email: string) => (
                    <span
                      key={email}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                    >
                      <EnvelopeIcon className="w-3 h-3 mr-1 text-gray-400" />
                      {email}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action buttons. Edit + Delete are gated on
                  !last_sent_at (R9 2026-05-25) — sent schedules are
                  immutable. Duplicate (2026-06-04) shows for BOTH
                  sent and unsent rows because it creates a brand-new
                  schedule row with its own lifecycle.
                  Hidden while a delete-confirm is showing to avoid a
                  cluttered action cluster. */}
              {confirmDelete === schedule.id ? (
                <div className="flex gap-1">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => deleteMutation.mutate(schedule.id)}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmDelete(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  {!schedule.last_sent_at && (
                    <button
                      onClick={() => openEditForm(schedule)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                      title="Edit schedule"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => openDuplicateForm(schedule)}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-primary-700 hover:bg-primary-50 rounded inline-flex items-center gap-1"
                    title="Duplicate schedule (creates a new schedule with the same recipients and parameters)"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Duplicate
                  </button>
                  {!schedule.last_sent_at && (
                    <button
                      onClick={() => setConfirmDelete(schedule.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                      title="Delete schedule"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsTab;
